import React, { useMemo, useState } from 'react'
import { styled } from 'styled-components'
import { useSpring, animated, useSpringRef, useChain } from '@react-spring/web'
import type { SharedProps } from './types'
import {
	EOMS_BACKGROUND,
	EOMS_BORDER,
	EOMS_BORDER_RADIUS,
	EOMS_DARK_HIGHLIGHT,
} from './styles'
import {
	useFinalScore,
	useAverageScorePerStudent,
	startLocalSoundEffect,
	endLocalSoundEffect,
	useLocalDispatch,
} from '../../../context'
import {
	SCORE_DELAY_LENGTH,
	SCORE_LENGTH_OF_SOUND,
	SCORE_SLIDE_DURATION,
	SCORE_AVERAGE_FADE_IN_DURATION,
	SCORE_HEIGHT_CHANGE_DURATION,
} from './durations'
import { SPECIAL_EFFECT_IDS as SOUNDS } from '@mission.io/mission-toolkit/constants'
import { cssPlayerHeightUnit } from '../../../utility/constants'

const CONTAINER_HEIGHT_MIN = 20
const CONTAINER_HEIGHT_MAX = 26

const HEIGHT_PADDING_MIN = 2
const HEIGHT_PADDING_MAX = 3

/**
 * FinalScoreSelectingComponent - Displays the animated Score count up End Of Mission Sequence component. Sets up the props and
 *    guards against rerenders.
 *
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 *
 * @returns React$Node
 */
export default function FinalScoreSelectingComponent({
	started,
	onFinish,
}: SharedProps): React$Node {
	const finalScore = useFinalScore()
	const averageScorePerStudent = useAverageScorePerStudent() || 0
	return useMemo(() => {
		return (
			<FinalScoreComponent
				started={started}
				onFinish={onFinish}
				finalScore={finalScore}
				averageScorePerStudent={averageScorePerStudent}
			/>
		)
	}, [started, onFinish, finalScore, averageScorePerStudent])
}

/**
 * FinalScoreComponent - animated score component for the End Of Mission Sequence which counts up the score and average
 *    student score obtained during the mission. You should guard against rerendering this component so that the animations work correctly.
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 * @param  {?number} props.finalScore - the total score obtained by the whole class during the mission
 * @param  {?number} props.averageScorePerStudent - the average score of the students who participated in the mission
 *
 * @returns React$Node
 */
function FinalScoreComponent({
	finalScore,
	averageScorePerStudent,
	started,
	onFinish,
}: {
	averageScorePerStudent: number,
	finalScore: ?number,
	...SharedProps,
}): React$Node {
	const dispatch = useLocalDispatch()

	const [showAverage, setShowAverage] = useState(false)

	const pointRef = useSpringRef()
	const { score } = useSpring({
		score: finalScore,
		from: { score: 0 },
		reset: false,
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_COUNT_UP_CLICKS))
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUSPENSE))
		},
		onRest: () => {
			dispatch(endLocalSoundEffect(SOUNDS.EOMS_COUNT_UP_CLICKS))
			dispatch(endLocalSoundEffect(SOUNDS.EOMS_SUSPENSE))
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SUPER_SPARKLE))
		},
		ref: pointRef,
		config: { duration: SCORE_LENGTH_OF_SOUND },
	})

	const slideRef = useSpringRef()
	const slideStyles = useSpring({
		ref: slideRef,
		to: { marginLeft: '0%' },
		from: { marginLeft: '25%' },
		config: { duration: SCORE_SLIDE_DURATION },
		delay: SCORE_DELAY_LENGTH,
		onRest: () => {
			setShowAverage(true)
		},
	})

	const averageRef = useSpringRef()
	const averageScore = useSpring({
		ref: averageRef,
		to: { opacity: 1 },
		from: { opacity: 0 },
		config: { duration: SCORE_AVERAGE_FADE_IN_DURATION },
		onStart: () => {
			dispatch(startLocalSoundEffect(SOUNDS.EOMS_SUCCESS_SPARKLE))
		},
	})

	const heightRef = useSpringRef()
	const heightStyles = useSpring({
		ref: heightRef,
		from: { heightProportion: 0 },
		to: { heightProportion: 1 },
		config: { duration: SCORE_HEIGHT_CHANGE_DURATION },
		onRest: onFinish,
	})

	useChain(started ? [pointRef, slideRef, averageRef, heightRef] : [])

	return (
		<Body
			style={{
				height: heightStyles.heightProportion.to(proportion =>
					interpolatePH(CONTAINER_HEIGHT_MAX, CONTAINER_HEIGHT_MIN, proportion)
				),
			}}>
			<AnimatedInline style={slideStyles}>
				<FinalScoreContainer>
					<Header
						style={{
							height: heightStyles.heightProportion.to(proportion =>
								interpolatePH(
									HEIGHT_PADDING_MAX,
									HEIGHT_PADDING_MIN,
									proportion
								)
							),
						}}>
						FINAL MISSION SCORE
					</Header>
					<DataAligner>
						{finalScore != null ? (
							<AnimatedData>
								{score.interpolate(val =>
									Number(val.toFixed()).toLocaleString()
								)}
							</AnimatedData>
						) : (
							<Data>N / A</Data>
						)}
					</DataAligner>
				</FinalScoreContainer>
			</AnimatedInline>
			{showAverage ? (
				<Inline>
					<AnimatedAverageScore style={averageScore}>
						<Header
							style={{
								height: heightStyles.heightProportion.to(proportion =>
									interpolatePH(
										HEIGHT_PADDING_MAX,
										HEIGHT_PADDING_MIN,
										proportion
									)
								),
							}}>
							AVERAGE STUDENT SCORE
						</Header>
						<DataAligner>
							<Data>
								{Number(averageScorePerStudent.toFixed()).toLocaleString()}
							</Data>
						</DataAligner>
					</AnimatedAverageScore>
				</Inline>
			) : null}
		</Body>
	)
}

/**
 * interpolatePH - interpolate between two values in player height units (ph)
 *
 * @param  {number} start - the starting value in ph
 * @param  {number} end - the ending value in ph
 * @param  {number} proportion - the proportion [0 - 1] of the way through the interpolation
 *
 * @returns number - the interpolated value
 */
function interpolatePH(start: number, end: number, proportion: number): string {
	return cssPlayerHeightUnit(start + (end - start) * proportion)
}

const Body = animated(styled.div`
	width: 80%;
	margin-bottom: ${cssPlayerHeightUnit(3.5)};
	height: ${cssPlayerHeightUnit(26)};
	background-color: ${EOMS_BACKGROUND};
	border: ${EOMS_BORDER};
	border-radius: ${EOMS_BORDER_RADIUS};
	display: flex;
`)

const Inline = styled.div`
	display: inline-block;
	width: 50%;
	height: 100%;
`

const FinalInline = styled(Inline)`
	display: block;
`

const FinalScoreContainer = styled.div`
	text-align: center;
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
`

const Data = styled.div`
	font-size: ${cssPlayerHeightUnit(5)};
	font-weight: bold;
	padding: ${cssPlayerHeightUnit(1)};
	background-color: ${EOMS_DARK_HIGHLIGHT};
	border-radius: ${EOMS_BORDER_RADIUS};
	min-width: 50%;
`

const DataAligner = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`

const AnimatedData = animated(Data)

const AverageScoreContainer = styled(FinalScoreContainer)`
	border-left: ${EOMS_BORDER};
	opacity: 0;
	margin-left: 0;
	height: 100%;
`

const Header = animated(styled.div`
	font-size: ${cssPlayerHeightUnit(3)};
	margin-bottom: ${cssPlayerHeightUnit(3)};
	font-weight: bold;
`)

const AnimatedInline = animated(FinalInline)
const AnimatedAverageScore = animated(AverageScoreContainer)
